import { onDestroy, onView } from "@/modules/_";
import gsap, { reduced } from "@lib/gsap";
import { Scroll } from "@lib/scroll";
import { handleEditor } from "@webflow/detect-editor";

// Continuous spin of the child element via a GSAP tween. `petal-spin` sets the
// direction (right = clockwise, left = counter-clockwise), `petal-speed` the
// deg/sec. Scroll steers the tween's timeScale: sign = direction (a reversal flips
// the spin and it keeps going that way), magnitude = speed.
const DEFAULT_SPEED = 12; // deg/sec at timeScale 1
const SCROLL_TS_MULT = 0.18; // extra timeScale per unit of Lenis velocity
const MAX_TS = 24; // clamp the timeScale magnitude
const SCROLL_EPS = 0.05; // ignore idle jitter so it can't flip direction
const SMOOTH = 0.5; // sec for timeScale to ease toward a new target
const RELAX_DELAY = 0.12; // sec after scrolling stops before easing back to base speed
const RUNWAY = 1e6; // playhead offset so a reversed (negative timeScale) loop never hits t=0

export default function (element: HTMLElement, _dataset: DOMStringMap) {
  const child = element.querySelector<HTMLElement>(":scope > *");
  if (!child || reduced) return;

  // Plain attributes (no `data-` prefix), so read them off the element, not dataset.
  const dir = element.getAttribute("petal-spin") === "left" ? -1 : 1;
  const speed = Number(element.getAttribute("petal-speed")) || DEFAULT_SPEED;

  gsap.set(child, { transformOrigin: "center center" });

  const spin = gsap.to(child, {
    rotation: "+=360",
    duration: 360 / speed,
    ease: "none",
    repeat: -1,
  });
  spin.totalTime(RUNWAY); // runway for reversing without stalling at t=0
  spin.timeScale(dir);

  // Tweening timeScale through 0 makes a scroll reversal glide instead of snap.
  const setTimeScale = gsap.quickTo(spin, "timeScale", {
    duration: SMOOTH,
    ease: "power2.out",
  });

  const clampTS = gsap.utils.clamp(-MAX_TS, MAX_TS);

  let flip = 1; // persistent scroll-driven direction (+1 / −1)

  // Ease back to base speed (keeping direction) once scrolling stops.
  const relax = gsap
    .delayedCall(RELAX_DELAY, () => setTimeScale(dir * flip))
    .pause();

  const offScroll = Scroll.add(({ velocity }: { velocity: number }) => {
    if (velocity > SCROLL_EPS) flip = 1;
    else if (velocity < -SCROLL_EPS) flip = -1;

    const boost = Math.abs(velocity) * SCROLL_TS_MULT;
    setTimeScale(clampTS(dir * flip * (1 + boost)));
    relax.restart(true);
  });

  // Pause off-screen and in the Webflow Designer.
  let inView = true;
  let editorPaused = false;
  const sync = () => {
    if (inView && !editorPaused) spin.play();
    else spin.pause();
  };
  sync();

  onView(element, {
    autoStart: true,
    callback: ({ isIn }) => {
      inView = isIn;
      sync();
    },
  });

  handleEditor((isEditor) => {
    editorPaused = isEditor;
    sync();
  });

  onDestroy(() => {
    offScroll();
    relax.kill();
    spin.kill();
  });
}
